import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { AuthProvider } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import Login from './pages/Login';
import AdminDashboard from './pages/admin/Dashboard';
import AdminCupones from './pages/admin/Cupones';
import AdminComercios from './pages/admin/Comercios';
import ComerciosExtendidos from './pages/admin/ComerciosExtendidos';
import ComercioForm from './pages/admin/ComercioForm';
import ComercioLocales from './pages/admin/ComercioLocales';
import AdminComercioCampanas from './pages/admin/ComercioCampanas';
import ComercioDashboard from './pages/comercio/Dashboard';
import ComercioRedenciones from './pages/comercio/Redenciones';
import ComercioCampanas from './pages/comercio/Campanas';
import VerificacionDocumento from './pages/comercio/VerificacionDocumento';
import UsuariosComunidad from './pages/admin/UsuariosComunidad';
import QRScanner from './pages/QRScanner';
import Unauthorized from './pages/Unauthorized';
import NotFound from './pages/NotFound';

// Tema personalizado
const theme = createTheme({
  palette: {
    primary: {
      main: '#2E7D32', // Verde
      light: '#66BB6A',
      dark: '#1B5E20',
    },
    secondary: {
      main: '#FF8F00', // Naranja
      light: '#FFB74D',
      dark: '#E65100',
    },
    background: {
      default: '#F5F5F5',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        },
      },
    },
  },
});

const App: React.FC = () => {
  const recaptchaSiteKey = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LebCIIrAAAAAITsw-KZXKmVTl17Rw1Po63bsnQT';

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GoogleReCaptchaProvider reCaptchaKey={recaptchaSiteKey}>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Ruta raíz - redirige según autenticación */}
              <Route path="/" element={<Navigate to="/login" replace />} />
              
              {/* Login */}
              <Route path="/login" element={<Login />} />
              
              {/* Rutas de Admin */}
              <Route path="/admin" element={
                <ProtectedRoute requireRole="admin">
                  <AdminDashboard />
                </ProtectedRoute>
              } />
              <Route path="/admin/cupones" element={
                <ProtectedRoute requireRole="admin">
                  <AdminCupones />
                </ProtectedRoute>
              } />
              <Route path="/admin/comercios" element={
                <ProtectedRoute requireRole="admin">
                  <AdminComercios />
                </ProtectedRoute>
              } />
              
              {/* Nuevas rutas de comercios extendidos */}
              <Route path="/admin/comercios-ext" element={
                <ProtectedRoute requireRole="admin">
                  <ComerciosExtendidos />
                </ProtectedRoute>
              } />
              <Route path="/admin/comercios-ext/nuevo" element={
                <ProtectedRoute requireRole="admin">
                  <ComercioForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/comercios-ext/:id/editar" element={
                <ProtectedRoute requireRole="admin">
                  <ComercioForm />
                </ProtectedRoute>
              } />
              <Route path="/admin/comercios-ext/:id/locales" element={
                <ProtectedRoute requireRole="admin">
                  <ComercioLocales />
                </ProtectedRoute>
              } />
              <Route path="/admin/comercios-ext/:id/campanas" element={
                <ProtectedRoute requireRole="admin">
                  <AdminComercioCampanas />
                </ProtectedRoute>
              } />
              
              {/* Usuarios de la Comunidad WordPress */}
              <Route path="/admin/usuarios-comunidad" element={
                <ProtectedRoute requireRole="admin">
                  <UsuariosComunidad />
                </ProtectedRoute>
              } />
              
              {/* Rutas de Comercio */}
              <Route path="/comercio" element={
                <ProtectedRoute requireRole="comercio">
                  <ComercioDashboard />
                </ProtectedRoute>
              } />
              <Route path="/comercio/redenciones" element={
                <ProtectedRoute requireRole="comercio">
                  <ComercioRedenciones />
                </ProtectedRoute>
              } />
              <Route path="/comercio/campanas" element={
                <ProtectedRoute requireRole="comercio">
                  <ComercioCampanas />
                </ProtectedRoute>
              } />
              <Route path="/comercio/verificacion-documento" element={
                <ProtectedRoute requireRole="comercio">
                  <VerificacionDocumento />
                </ProtectedRoute>
              } />
              
              {/* Escáner QR - accesible para todos los autenticados */}
              <Route path="/scanner" element={
                <ProtectedRoute>
                  <QRScanner />
                </ProtectedRoute>
              } />
              
              {/* Validación de cupón desde QR */}
              <Route path="/validar/:codigo" element={
                <ProtectedRoute>
                  <QRScanner />
                </ProtectedRoute>
              } />
              
              {/* Páginas de error */}
              <Route path="/unauthorized" element={<Unauthorized />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
        </AuthProvider>
      </GoogleReCaptchaProvider>
    </ThemeProvider>
  );
};

export default App;
